import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useApi } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useTenant } from "../lib/tenant";
import { useRealtime } from "../lib/useRealtime";

type Step = {
  id: string; title: string; prompt: string; kind: string;
  options?: { id: string; label: string }[];
  min?: number; max?: number;
};

type State = {
  onboarding_id: string;
  tenant_id: string;
  status: "researching" | "awaiting_user" | "proposing" | "approved";
  step_idx: number;
  profile: {
    company_name: string; website: string | null;
    industry: string | null; size_range: string | null; region: string | null;
    summary: string; enrichment: Record<string, any>;
    research_log: { ts: string; tool: string; label: string; status: string }[];
  };
  answers: Record<string, any>;
  proposal: {
    persona: string; industry: string; rationale: string;
    kpis: { id: string; name: string; unit: string; benchmark: number | null; score: number }[];
  } | null;
  created_at: string; updated_at: string;
};

const DEFAULT_STEPS: Step[] = [
  {
    id: "persona",
    title: "Welche Rolle soll zuerst unterstützt werden?",
    prompt: "Wählen Sie die primäre C-Level-Perspektive für die erste KPI-Analyse.",
    kind: "choice",
    options: [
      { id: "ceo", label: "CEO / Geschäftsführung" },
      { id: "cfo", label: "CFO / Finance" },
      { id: "coo", label: "COO / Operations" },
      { id: "sales", label: "Sales Leadership" },
    ],
  },
  {
    id: "goals",
    title: "Welche Ziele stehen im Vordergrund?",
    prompt: "Priorisieren Sie die Geschäftsziele, an denen KPI Enterprise Mining die Steuerung ausrichten soll.",
    kind: "multi_choice",
    min: 1,
    max: 3,
    options: [
      { id: "growth", label: "Wachstum und Pipeline" },
      { id: "margin", label: "Marge und Profitabilität" },
      { id: "cash", label: "Cashflow und Working Capital" },
      { id: "delivery", label: "Lieferfähigkeit und Qualität" },
      { id: "risk", label: "Risiko, Compliance und Audit" },
    ],
  },
  {
    id: "pain_points",
    title: "Wo ist die Steuerung heute zu langsam?",
    prompt: "Diese Auswahl steuert, welche Agents und KPI-Kandidaten zuerst aktiviert werden.",
    kind: "multi_choice",
    min: 1,
    max: 3,
    options: [
      { id: "manual_reports", label: "Manuelle Reports" },
      { id: "unclear_metrics", label: "Uneinheitliche KPI-Definitionen" },
      { id: "late_anomalies", label: "Abweichungen werden zu spät erkannt" },
      { id: "missing_owners", label: "Maßnahmen ohne Owner" },
      { id: "weak_benchmarks", label: "Keine belastbaren Benchmarks" },
    ],
  },
  {
    id: "data_sources",
    title: "Welche Datenquellen sind verfügbar?",
    prompt: "KPI-Werte werden erst nach Source- und Quality-Gate produktiv angezeigt.",
    kind: "multi_choice",
    min: 1,
    max: 4,
    options: [
      { id: "erp", label: "ERP / Finance" },
      { id: "crm", label: "CRM / Sales" },
      { id: "web", label: "Website / Marketing" },
      { id: "warehouse", label: "DWH / BI" },
      { id: "uploads", label: "Excel / CSV Uploads" },
    ],
  },
];

export default function OnboardingConversationPage() {
  const api = useApi();
  const nav = useNavigate();
  const { mode } = useAuth();
  const { effectiveTenantId } = useTenant();
  const { events } = useRealtime();
  const [steps, setSteps] = useState<Step[]>([]);
  const [state, setState] = useState<State | null>(null);
  const [companyInput, setCompanyInput] = useState({ company_name: "", website: "" });
  const [singleChoice, setSingleChoice] = useState<string>("");
  const [multiChoice, setMultiChoice] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const effectiveSteps = steps.length > 0 ? steps : DEFAULT_STEPS;

  useEffect(() => {
    api.get<Step[]>("/onboarding/steps")
      .then((items) => setSteps(items.length > 0 ? items : DEFAULT_STEPS))
      .catch(() => setSteps(DEFAULT_STEPS));
  }, [api]);

  // Poll while researching
  useEffect(() => {
    if (!state) return;
    if (state.status !== "researching") return;
    const t = setInterval(async () => {
      try {
        const s = await api.get<State>(`/onboarding/${state.onboarding_id}`);
        setState(s);
      } catch (e) { console.error(e); }
    }, 1200);
    return () => clearInterval(t);
  }, [state?.onboarding_id, state?.status, api]);

  // also merge realtime onboarding_research events
  useEffect(() => {
    if (!state) return;
    const relevant = events.filter((e) =>
      (e.type === "onboarding_research" || e.type === "onboarding_step") &&
      e.payload?.onboarding_id === state.onboarding_id
    );
    if (relevant.length > 0 && state.status === "researching") {
      // re-fetch state
      api.get<State>(`/onboarding/${state.onboarding_id}`).then(setState).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events.length]);

  async function startResearch() {
    if (!companyInput.company_name.trim()) return;
    setSubmitting(true);
    try {
      const s = await api.post<State>("/onboarding/start", {
        company_name: companyInput.company_name,
        website: companyInput.website || null,
      });
      setState(s);
    } finally { setSubmitting(false); }
  }

  async function submitAnswer(stepId: string, value: any) {
    if (!state) return;
    setSubmitting(true);
    try {
      const s = await api.post<State>(`/onboarding/${state.onboarding_id}/answer`, { step_id: stepId, value });
      setState(s);
      setSingleChoice(""); setMultiChoice([]);
    } finally { setSubmitting(false); }
  }

  async function approve() {
    if (!state) return;
    setSubmitting(true);
    try {
      await api.post<State>(`/onboarding/${state.onboarding_id}/approve`, {});
      nav("/app/command-center");
    } finally { setSubmitting(false); }
  }

  // Render the welcome step
  if (!state) {
    return (
      <div className="onb-shell">
        <OnboardingHeader step={0} total={steps.length} />
        <main className="onb-stage">
          <span className="hero-eyebrow">Schritt 1 von {effectiveSteps.length}</span>
          <h1>Wir lernen Ihr Unternehmen kennen.</h1>
          <p className="lead">
            Sobald Sie unten den Firmennamen und (optional) die Website eingeben, startet unser
            Research-Agent im Hintergrund. Sie sehen Schritt für Schritt was er findet.
          </p>
          <form className="onb-form" onSubmit={(e) => { e.preventDefault(); void startResearch(); }}>
            <label>
              <span>Firmenname *</span>
              <input
                autoFocus
                placeholder="z.B. Acme Mittelstand GmbH"
                value={companyInput.company_name}
                onChange={(e) => setCompanyInput({ ...companyInput, company_name: e.target.value })}
              />
            </label>
            <label>
              <span>Website (optional)</span>
              <input
                placeholder="https://www.acme-mittelstand.de"
                value={companyInput.website}
                onChange={(e) => setCompanyInput({ ...companyInput, website: e.target.value })}
              />
            </label>
            <button type="submit" className="btn primary big" disabled={submitting || !companyInput.company_name.trim()}>
              {submitting ? "Research startet ..." : "Research starten"}
            </button>
          </form>
          <p className="muted small">
            Tenant: <code>{effectiveTenantId}</code> ·{" "}
            {mode === "demo"
              ? "Sandbox-Demo: Der Agent arbeitet mit Beispieldaten."
              : "Live-Tenant: Der Agent arbeitet nur mit den Daten und Quellen dieses Mandanten."}
          </p>
        </main>
      </div>
    );
  }

  // Render researching step
  if (state.status === "researching") {
    return (
      <div className="onb-shell">
        <OnboardingHeader step={1} total={steps.length} />
        <main className="onb-stage">
          <span className="hero-eyebrow">Schritt 2 von {effectiveSteps.length}</span>
          <h1>Research-Agent recherchiert …</h1>
          <p className="lead">Wir scannen Website, prüfen Wikidata, lesen Branchen-Benchmark. Live unten.</p>
          <ul className="research-log">
            {state.profile.research_log.map((r, i) => (
              <li key={i} className={`research-line status-${r.status}`}>
                <span className={`research-dot ${r.status}`}></span>
                <strong>{r.tool}</strong>
                <span>{r.label}</span>
                <time className="muted small">{new Date(r.ts).toLocaleTimeString()}</time>
              </li>
            ))}
            {state.profile.research_log.length < 4 && (
              <li className="research-line status-pending"><span className="research-dot running"></span><span className="muted">… nächster Schritt lädt</span></li>
            )}
          </ul>
        </main>
      </div>
    );
  }

  // Proposing step
  if (state.status === "proposing" && state.proposal) {
    return (
      <div className="onb-shell">
        <OnboardingHeader step={effectiveSteps.length} total={effectiveSteps.length} />
        <main className="onb-stage">
          <span className="hero-eyebrow">Vorschlag</span>
          <h1>Ihre maßgeschneiderten KPIs</h1>
          <p className="lead">{state.proposal.rationale}</p>
          <ul className="kpi-proposal">
            {state.proposal.kpis.map((k, i) => (
              <li key={k.id}>
                <span className="kpi-num">{i + 1}</span>
                <div className="kpi-body">
                  <strong>{k.name}</strong>
                  <span className="muted small">Einheit: {k.unit}{k.benchmark != null ? ` · Branchen-Median: ${k.benchmark}` : ""}</span>
                </div>
              </li>
            ))}
          </ul>
          <div className="onb-actions">
            <button className="btn primary big" onClick={approve} disabled={submitting}>
              {submitting ? "Übernehme ..." : "Übernehmen und ins Cockpit"}
            </button>
            <Link to="/" className="btn ghost">Abbrechen</Link>
          </div>
        </main>
      </div>
    );
  }

  // Approved (rare — we usually navigate away)
  if (state.status === "approved") {
    return <div className="onb-shell"><main className="onb-stage"><h1>Willkommen!</h1><p>Bereit. <Link to="/app/command-center">Zum Command Center</Link></p></main></div>;
  }

  // awaiting_user — show current step (persona | goals | painpoints | data)
  const step = effectiveSteps[state.step_idx];
  if (!step) return null;

  return (
    <div className="onb-shell">
      <OnboardingHeader step={state.step_idx} total={effectiveSteps.length} />
      <main className="onb-stage">
        <span className="hero-eyebrow">Schritt {state.step_idx + 1} von {effectiveSteps.length}</span>
        <h1>{step.title}</h1>
        <p className="lead">{step.prompt}</p>

        {step.kind === "choice" && step.options && (
          <div className="onb-choices">
            {step.options.map((o) => (
              <button
                key={o.id}
                className={`onb-choice ${singleChoice === o.id ? "active" : ""}`}
                onClick={() => setSingleChoice(o.id)}
              >
                {o.label}
              </button>
            ))}
            <div className="onb-actions">
              <button className="btn primary big" disabled={!singleChoice || submitting} onClick={() => submitAnswer(step.id, singleChoice)}>
                Weiter
              </button>
            </div>
          </div>
        )}

        {step.kind === "multi_choice" && step.options && (
          <div className="onb-choices">
            {step.options.map((o) => {
              const on = multiChoice.includes(o.id);
              return (
                <button
                  key={o.id}
                  className={`onb-choice ${on ? "active" : ""}`}
                  onClick={() => {
                    if (on) setMultiChoice(multiChoice.filter((x) => x !== o.id));
                    else if (multiChoice.length < (step.max || 99)) setMultiChoice([...multiChoice, o.id]);
                  }}
                >
                  {o.label}
                </button>
              );
            })}
            <div className="onb-actions">
              <span className="muted small">{multiChoice.length} von {step.max || "?"} ausgewählt</span>
              <button className="btn primary big" disabled={multiChoice.length < (step.min || 1) || submitting} onClick={() => submitAnswer(step.id, multiChoice)}>
                Weiter
              </button>
            </div>
          </div>
        )}
      </main>
      <aside className="onb-sidebar">
        <h3>Was wir bisher wissen</h3>
        <dl>
          <dt>Unternehmen</dt><dd>{state.profile.company_name}</dd>
          {state.profile.website && <><dt>Website</dt><dd>{state.profile.website}</dd></>}
          {state.profile.industry && <><dt>Branche</dt><dd>{state.profile.industry}</dd></>}
          {state.profile.size_range && <><dt>Größe</dt><dd>{state.profile.size_range}</dd></>}
          {state.profile.region && <><dt>Region</dt><dd>{state.profile.region}</dd></>}
        </dl>
        {state.profile.summary && <p className="muted small">{state.profile.summary}</p>}
      </aside>
    </div>
  );
}

function OnboardingHeader({ step, total }: { step: number; total: number }) {
  const pct = total > 0 ? Math.min(100, Math.round((step / Math.max(1, total - 1)) * 100)) : 0;
  return (
    <header className="onb-header">
      <Link to="/" className="onb-brand">KPI Enterprise Mining</Link>
      <div className="onb-progress">
        <div className="onb-progress-track"><div className="onb-progress-fill" style={{ width: `${pct}%` }} /></div>
        <span className="muted small">{pct}%</span>
      </div>
    </header>
  );
}
